// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductContextAvailability from "@stoneage/Overrides/Components/ProductContextAvailability";
import React, { useState } from "react";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { xIsAuthenticated } from "@stoneage/App/Store/Selectors/GeneralSelectors";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { parseBoolean } from "@stoneage/XDL/Utilities";
import { getSettingsCollection } from "@stoneage/App/Store/Selectors/SettingsSelectors";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import ProductPrice, { ProductPriceStyles } from "@stoneage/Overrides/Components/ProductPrice";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { connect } from "react-redux";
import { css } from "styled-components";
import { fontConfig } from "@stoneage/XDL/constants";
import IconGear from "@stoneage/XDL/Icons/IconGear";
import { ConfigurationType } from "@insite/client-framework/Services/ProductServiceV2";

interface OwnProps {
    showPrice?: boolean;
    showAvailability?: boolean;
    isProductDetailsPage?: boolean;
    configurationCompleted?: boolean;
    extendedStyles?: CustomProductPriceStyles;
}

const mapStateToProps = (state: CustomAppState) => ({
    isAuthenticated: xIsAuthenticated(state),
    productSettings: getSettingsCollection(state).productSettings,
    hideProductBundleOptions: getSettingsCollection(state).customSettings.hideProductBundleOptions,
    currency: getSession(state).currency,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

export interface CustomProductPriceStyles {
    container?: InjectableCss;
    priceReplacementContainer?: InjectableCss;
    availabilityContainer?: InjectableCss;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    availabilityStyles?: ProductAvailabilityStyles;
    replacementInventoryText?: TypographyPresentationProps;
    replacementInventoryIcon?: IconPresentationProps;
    replacementPriceText?: TypographyPresentationProps;
    price?: ProductPriceStyles;
}

const customProductPriceStyles: CustomProductPriceStyles = {
    container: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            gap: 4px;
            & .product-price-savings-message {
                margin-bottom: 4px;
            }
        `,
    },
    priceReplacementContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 4px;
        `,
    },
    replacementPriceText: {
        variant: "h4",
        css: css`
            margin-bottom: 0;
        `,
    },
    replacementInventoryText: {
        ...fontConfig.xSmallSingleLine,
        weight: fontConfig.weight.regular,
        css: css`
            display: flex;
            align-items: center;
            gap: 4px;
        `,
    },
    replacementInventoryIcon: {
        size: 12,
        src: IconGear,
        color: "common.ui6",
    },
};

function isConfigurableType(type?: string): boolean {
    return !!type && type !== ConfigurationType.None && type !== ConfigurationType.Fixed;
}

const CustomProductPrice = ({
    productContext,
    isAuthenticated,
    showPrice,
    showAvailability,
    hideProductBundleOptions,
    productSettings,
    currency,
    isProductDetailsPage,
    configurationCompleted,
    extendedStyles,
}: Props) => {
    const [styles] = useState(() => mergeToNew(customProductPriceStyles, extendedStyles));

    if (!isAuthenticated || !showPrice || !productContext.product) {
        return null;
    }

    const { product, productInfo } = productContext;
    const isConfigurableProduct = isConfigurableType(product.properties.configurationType || product.configurationType);

    const shouldShowReplacement = product.isVariantParent || isConfigurableProduct;
    const shouldHideInventoryForBundleProducts =
        parseBoolean(hideProductBundleOptions) &&
        parseBoolean(product.properties["hideBundleOptions"]) &&
        isConfigurableProduct;

    const pricingRender = () => {
        const currencySymbol = currency?.currencySymbol || currency?.currencyCode;
        return (
            <>
                {!productInfo.pricing || productInfo.pricing?.regularPrice === 0 ? (
                    <Typography {...styles.replacementPriceText} translate="no" data-test-selector="NoPrice">
                        {product.properties.startingFromPrice
                            ? product.properties.startingFromPrice
                            : `${currencySymbol}–.––
`}
                    </Typography>
                ) : (
                    <ProductPrice
                        product={productContext}
                        showLabel={false}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        extendedStyles={styles.price}
                        // XNG-Changes
                        showUnitOfMeasure={true}
                    />
                )}
            </>
        );
    };

    // XNG-Changes
    const replacementInventoryRender = () => {
        return (
            <Typography {...styles.replacementInventoryText} data-test-selector="replacementInventoryText">
                <Icon {...styles.replacementInventoryIcon} />
                {siteMessage("Custom_Inventory_ConfigureForAvailability", "Configure for Availability")}
            </Typography>
        );
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="CustomProductPrice">
            {shouldShowReplacement ? (
                <StyledWrapper
                    {...styles.priceReplacementContainer}
                    data-test-selector="priceReplacementContainer-shouldShowReplacement"
                >
                    {pricingRender()}
                    {!shouldHideInventoryForBundleProducts && <>{replacementInventoryRender()}</>}
                </StyledWrapper>
            ) : (
                <>
                    <StyledWrapper
                        {...styles.priceReplacementContainer}
                        data-test-selector="priceReplacementContainer-pricingRender"
                    >
                        {pricingRender()}
                        {!shouldHideInventoryForBundleProducts &&
                            shouldShowReplacement &&
                            (!productInfo.pricing || productInfo.pricing?.regularPrice === 0) &&
                            isAuthenticated && <>{replacementInventoryRender()}</>}
                        <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                    </StyledWrapper>
                    {productSettings.showInventoryAvailability && showAvailability && (
                        <StyledWrapper
                            {...styles.availabilityContainer}
                            data-test-selector="CustomProductPrice-availabilityContainer"
                        >
                            <ProductContextAvailability
                                isProductDetailsPage={isProductDetailsPage}
                                configurationCompleted={configurationCompleted}
                                extendedStyles={styles.availabilityStyles}
                            />
                        </StyledWrapper>
                    )}
                </>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withProductContext(CustomProductPrice));
